import { useEffect, useRef, type RefObject } from 'react';
import './LazyListFooter.css';

export default function LazyListFooter({ rootRef, count, total, noun, loading, error, hasMore, onLoadMore, onRetry }: {
  rootRef: RefObject<HTMLElement>;
  count: number;
  total?: number;
  noun: 'users' | 'activities';
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
}) {
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!hasMore || loading || error || !sentinel.current || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) onLoadMore();
    }, { root: rootRef.current, rootMargin: '0px 0px 160px 0px' });
    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [hasMore, loading, error, onLoadMore, rootRef]);

  return <div className="lazy-list-footer" ref={sentinel}>
    {count > 0 && <span className="lazy-list-count">{total !== undefined
      ? `Showing ${count.toLocaleString()} of ${total.toLocaleString()} ${noun}`
      : `${count.toLocaleString()} ${noun} loaded`}{!hasMore && !loading && !error ? ' · End of list' : ''}</span>}
    {error ? <div role="alert" className="lazy-list-error">
      <span>Unable to load {noun === 'activities' ? 'activity data' : 'users'}. {error}</span>
      <button type="button" onClick={onRetry}>Retry</button>
    </div> : loading ? <span role="status">{count ? 'Loading more…' : 'Loading…'}</span>
      : hasMore ? <button type="button" onClick={onLoadMore}>{noun === 'activities' ? 'Load older activity' : 'Load more users'}</button> : null}
  </div>;
}
